'use client';

import Link from '@/components/auth/LoginLink';
import { StudioAction } from '@/components/ui/StudioAction';
import { LiquidGlass } from '@/components/ui/liquid-glass/LiquidGlass';
import controls from './PublicSiteControls.module.css';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { BrandWordmark } from '@/components/common/BrandWordmark';
import { buildLocalizedSeoPath, type SeoLocale } from '@/lib/seo/site';
import styles from './PublicContent.module.css';

export interface SeoPublicHeaderMessages {
  nav: {
    label: string; useCases: string; resources: string; guides: string; pricing: string;
    language: string; languageChinese: string; languageEnglish: string; startFree: string;
  };
}
interface SeoPublicHeaderProps {
  locale: SeoLocale;
  messages: SeoPublicHeaderMessages;
  startHref?: string;
  startAction?: () => void;
  homeHref?: string;
  useCasesHref?: string;
  showLanguageSelector?: boolean;
  languagePath?: string;
  contactLabel?: string;
  layout?: 'standard' | 'homepage';
}

export function SeoPublicHeader({ locale, messages, startHref = '/login?redirect=%2Fexplore&login=1', startAction, homeHref, useCasesHref, layout = 'standard' }: SeoPublicHeaderProps) {
  const localize = (path: string) => buildLocalizedSeoPath(path, locale);
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const resourcesRef = useRef<HTMLDetailsElement>(null);
  const menuId = useId();
  useEffect(() => {
    const dismiss = (event: PointerEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) setMenuOpen(false);
      if (resourcesRef.current && !resourcesRef.current.contains(event.target as Node)) resourcesRef.current.open = false;
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (menuOpen) {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      } else if (resourcesRef.current?.open) {
        resourcesRef.current.open = false;
        const summary = resourcesRef.current.querySelector('summary');
        if (summary?.getClientRects().length) summary.focus();
      } else return;
      event.preventDefault();
      event.stopPropagation();
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', dismiss); document.removeEventListener('keydown', escape); };
  }, [menuOpen]);
  const start = startAction ? (
    <StudioAction variant="primary" size="compact" onClick={() => { setMenuOpen(false); startAction(); }} className={styles.compactAction}>{messages.nav.startFree}</StudioAction>
  ) : (
    <StudioAction href={localize(startHref)} variant="primary" size="compact" onClick={() => setMenuOpen(false)} className={styles.compactAction}>{messages.nav.startFree}</StudioAction>
  );
  return (
    <header ref={headerRef} className={styles.header} data-homepage-header={layout === 'homepage' || undefined}>
      <nav className={styles.nav} aria-label={messages.nav.label}>
        <Link href={homeHref ?? localize('/')} aria-label="DOBIDOBI" className={styles.brand}>
          <BrandWordmark alt="DOBIDOBI" priority className="relative block h-8 w-[144px]" sizes="144px" />
        </Link>
        <div className={styles.desktopNav}>
          <Link href={useCasesHref ?? localize('/use-cases')}>{messages.nav.useCases}</Link>
          <details ref={resourcesRef} className={styles.resources} onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) event.currentTarget.open = false;
          }}>
            <summary><LiquidGlass className={`${controls.button} ${controls.secondary} ${controls.compact} ${styles.resourcesTrigger}`}>{messages.nav.resources}<ChevronDown size={15} aria-hidden="true" /></LiquidGlass></summary>
            <div className={styles.resourceMenu}><LiquidGlass className={styles.floatingPanel}><Link href={localize('/guides')}>{messages.nav.guides}</Link></LiquidGlass></div>
          </details>
          <Link href={localize('/pricing')}>{messages.nav.pricing}</Link>
        </div>
        <div className={styles.headerActions}>
          <LanguageSwitcher currentLocale={locale} label={messages.nav.language} />
          <div className={styles.desktopStart}>{start}</div>
          <LiquidGlass as="button" ref={menuButtonRef} type="button" className={`${controls.button} ${controls.secondary} ${controls.icon} ${styles.menuButton}`} aria-label={messages.nav.label} aria-controls={menuId} aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
            {menuOpen ? <X size={20} aria-hidden /> : <Menu size={20} aria-hidden />}
          </LiquidGlass>
        </div>
      </nav>
      {menuOpen && <div className={styles.mobilePanel}><LiquidGlass className={styles.floatingPanel}><nav id={menuId} className={styles.mobileNav} aria-label={messages.nav.label}>
        <Link href={useCasesHref ?? localize('/use-cases')} onClick={() => setMenuOpen(false)}>{messages.nav.useCases}</Link>
        <Link href={localize('/guides')} onClick={() => setMenuOpen(false)}>{messages.nav.guides}</Link>
        <Link href={localize('/pricing')} onClick={() => setMenuOpen(false)}>{messages.nav.pricing}</Link>
        <div className={styles.mobileStart}>{start}</div>
      </nav></LiquidGlass></div>}
    </header>
  );
}
